import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  CheckCircle,
  Camera,
  Star,
  MapPin,
  ClipboardList,
  QrCode,
  AlertCircle,
  User,
  Users,
  MessageSquare,
  ListChecks,
  Send,
  Building2,
  X,
  Loader,
  Mail
} from 'lucide-react';
import AdminApp from './admin/AdminApp';
import { locationsApi, formsApi, authApi } from './services/api';

// Question Types
const QUESTION_TYPES = {
  1: { label: 'Açık Metin', icon: MessageSquare },
  2: { label: 'Yıldız Puanlama', icon: Star },
  3: { label: 'Çoktan Seçmeli', icon: ListChecks }
};

// Photo Requirements
const PHOTO_REQUIREMENTS = {
  1: 'disabled',
  2: 'optional',
  3: 'required'
};

// Footer Component
const Footer = () => (
  <footer className="text-center py-4 text-xs text-gray-400">
    © 2026 BOBO Yazılım · Tüm Hakları Saklıdır.
  </footer>
);

// Form Page Component - Main QR Landing Page
const FormPage = () => {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [availableForms, setAvailableForms] = useState([]);
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showFormSelector, setShowFormSelector] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotCaptchaToken, setForgotCaptchaToken] = useState(null);
  const forgotRecaptchaRef = useRef();
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null); // Store user name/email
  const [selectedFormRequiresLogin, setSelectedFormRequiresLogin] = useState(false);

  // Load persisted user data on mount
  useEffect(() => {
    const storedUserId = localStorage.getItem('personnelUserId');
    const storedUserData = localStorage.getItem('personnelUserData');
    const storedExpiry = localStorage.getItem('personnelSessionExpiry');

    if (storedUserId && storedUserData && storedExpiry && Date.now() < parseInt(storedExpiry, 10)) {
      setUserId(storedUserId);
      setUserData(JSON.parse(storedUserData));
    } else {
      // Clear expired session
      localStorage.removeItem('personnelUserId');
      localStorage.removeItem('personnelUserData');
      localStorage.removeItem('personnelSessionExpiry');
    }

    // Pre-fill remembered email
    const rememberedEmail = localStorage.getItem('personnelRememberedEmail');
    if (rememberedEmail) {
      setLoginData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (qrCode) {
      loadFormData();
    }
  }, [qrCode]);

  const loadFormData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get location and all available forms by QR code
      const data = await locationsApi.getByQrCode(qrCode);

      setLocation(data.location);
      setAvailableForms(data.forms);

      if (!data.forms || data.forms.length === 0) {
        throw new Error('Bu lokasyon için aktif form bulunamadı.');
      }

      setShowFormSelector(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectForm = (form) => {
    setSelectedFormRequiresLogin(form.isPersonelLoginRequired);

    if (form.isPersonelLoginRequired) {
      // If user is already logged in, verify they have access to this corporate
      if (userId && location?.corporateId) {
        const storedUserData = localStorage.getItem('personnelUserData') || sessionStorage.getItem('personnelUserData');
        if (storedUserData) {
          try {
            const userData = JSON.parse(storedUserData);
            const corporateIds = userData.corporateIds || [];
            // Check if user has access to this corporate
            if (!corporateIds.includes(location.corporateId)) {
              // Auto logout and show login screen
              handleLogout();
              setTemplate(form);
              setShowLogin(true);
              setLoginError('Bu kuruma erişim yetkiniz bulunmamaktadır. Lütfen geçerli hesapla giriş yapın.');
              return;
            }
          } catch (parseErr) {

          }
        }
      }

      // Show login if not already logged in
      if (!userId) {
        setTemplate(form);
        setShowLogin(true);
      } else {
        initializeForm(form);
      }
    } else {
      initializeForm(form);
    }
  };

  const initializeForm = (form) => {
    setTemplate(form);
    setShowFormSelector(false);

    // Initialize answers object
    const initialAnswers = {};
    form.questions?.forEach(q => {
      initialAnswers[q.id] = null;
    });
    setAnswers(initialAnswers);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);

    // Verify CAPTCHA
    if (!captchaToken) {
      setLoginError('Lütfen CAPTCHA doğrulamasını tamamlayın.');
      return;
    }

    setLoginLoading(true);
    try {
      const response = await authApi.login(loginData.email, loginData.password, captchaToken);
      const id = response.user?.id || response.userId || response.id;
      const name = response.user?.name || response.name || loginData.email;
      const email = response.user?.email || response.email || loginData.email;
      const corporateIds = response.user?.corporateIds || response.corporateIds || [];

      // Check if personnel is authorized for this corporate
      if (location?.corporateId && !corporateIds.includes(location.corporateId)) {
        throw new Error('Bu kuruma erişim yetkiniz bulunmamaktadır.');
      }

      // Persist user data based on rememberMe
      if (rememberMe) {
        const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
        localStorage.setItem('personnelUserId', id);
        localStorage.setItem('personnelUserData', JSON.stringify({ name, email, corporateIds }));
        localStorage.setItem('personnelSessionExpiry', expiry.toString());
        localStorage.setItem('personnelRememberedEmail', loginData.email);
      } else {
        // Clear any previous remember-me data
        localStorage.removeItem('personnelUserId');
        localStorage.removeItem('personnelUserData');
        localStorage.removeItem('personnelSessionExpiry');
        localStorage.removeItem('personnelRememberedEmail');
        // Store in sessionStorage for current session only
        sessionStorage.setItem('personnelUserId', id);
        sessionStorage.setItem('personnelUserData', JSON.stringify({ name, email, corporateIds }));
      }

      setUserId(id);
      setUserData({ name, email });
      setShowLogin(false);
      setShowFormSelector(false);
    } catch (err) {
      setLoginError(err.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
      // Reset CAPTCHA on failed login
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoginLoading(false);
    }
  };

  const openForgotPassword = () => {
    setForgotEmail(loginData.email);
    setForgotError('');
    setForgotSuccess(false);
    setForgotCaptchaToken(null);
    setShowForgotPassword(true);
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess(false);
    setForgotCaptchaToken(null);
    forgotRecaptchaRef.current?.reset();
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');

    if (!forgotCaptchaToken) {
      setForgotError('Lütfen CAPTCHA doğrulamasını tamamlayın.');
      return;
    }

    setForgotLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail, forgotCaptchaToken);
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(err.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
      forgotRecaptchaRef.current?.reset();
      setForgotCaptchaToken(null);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handlePhotoUpload = (questionId, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        setPhotos(prev => ({ ...prev, [questionId]: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (questionId) => {
    setPhotos(prev => {
      const newPhotos = { ...prev };
      delete newPhotos[questionId];
      return newPhotos;
    });
  };

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('personnelUserId');
    localStorage.removeItem('personnelUserData');
    localStorage.removeItem('personnelSessionExpiry');
    // Clear sessionStorage
    sessionStorage.removeItem('personnelUserId');
    sessionStorage.removeItem('personnelUserData');

    // Reset state
    setUserId(null);
    setUserData(null);
    setShowFormSelector(true);
    setError(null);
    setAnswers({});
    setPhotos({});
    setSubmitted(false);
    setTemplate(null);
  };

  const isFormComplete = () => {
    if (!template) return false;

    // Check all required questions are answered
    for (const question of template.questions || []) {
      if (question.isRequired && !answers[question.id]) {
        return false;
      }
      // Check required photos
      const photoReq = PHOTO_REQUIREMENTS[question.photoRequirement];
      if (photoReq === 'required' && !photos[question.id]) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormComplete()) return;

    setSubmitting(true);
    try {
      const submissionData = {
        formTemplateId: template.id,
        locationId: location.id,
        userId: userId,
        answers: template.questions?.map(q => ({
          FormQuestionId: q.id,
          answerText: answers[q.id]?.toString() || '',
          ratingValue: q.questionType === 2 ? parseInt(answers[q.id]) || 0 : null,
          selectedOptionId: q.questionType === 3 ? answers[q.id] : null,
          photoBase64: photos[q.id] || null
        })) || []
      };

      await formsApi.submitForm(submissionData);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Personnel Login Screen
  if (showLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <img src="/bobo-logo.png" alt="BOBO Yazılım" className="h-20 w-auto object-contain" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Personel Girişi</h1>
                <p className="text-gray-500">Devam etmek için giriş yapın</p>
              </div>

              {loginError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-700 text-sm">{loginError}</p>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-posta</label>
                  <input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="ornek@email.com"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Şifre</label>
                  <input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="personnelRememberMe"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <label htmlFor="personnelRememberMe" className="ml-2 text-sm text-gray-600 cursor-pointer select-none">
                      Beni Hatırla <span className="text-gray-400">(30 Gün)</span>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  >
                    Şifremi Unuttum
                  </button>
                </div>
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={(token) => setCaptchaToken(token)}
                    theme="light"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
                >
                  {loginLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                </button>
              </form>

              <button
                onClick={() => {
                  setShowLogin(false);
                  setTemplate(null);
                  setShowFormSelector(true);
                  setLoginError(null);
                  setLoginData({ email: '', password: '' });
                }}
                className="w-full mt-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
              >
                ← Geri Dön
              </button>
            </div>
          </div>
        </div>
        <Footer />

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Şifremi Unuttum</h2>
                <button onClick={closeForgotPassword} className="text-blue-200 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                {forgotSuccess ? (
                  <div className="text-center space-y-4">
                    <div className="flex justify-center">
                      <CheckCircle className="w-16 h-16 text-green-500" />
                    </div>
                    <p className="text-gray-700 font-medium">Yeni şifreniz telefon numaranıza gönderildi.</p>
                    <button
                      onClick={closeForgotPassword}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all"
                    >
                      Tamam
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Kayıtlı e-posta adresinizi girin. Yeni şifreniz kayıtlı telefon numaranıza SMS ile gönderilecektir.
                    </p>

                    {forgotError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{forgotError}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">E-posta Adresi</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="E-posta adresiniz"
                          required
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="flex justify-center">
                      <ReCAPTCHA
                        ref={forgotRecaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setForgotCaptchaToken(token)}
                        onExpired={() => setForgotCaptchaToken(null)}
                        theme="light"
                      />
                    </div>

                    <div className="flex gap-3 pt-1">
                      <button
                        type="button"
                        onClick={closeForgotPassword}
                        className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                      >
                        İptal
                      </button>
                      <button
                        type="submit"
                        disabled={forgotLoading}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {forgotLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Gönderiliyor...
                          </span>
                        ) : (
                          'Gönder'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Form Selector Screen
  if (showFormSelector && availableForms.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <img src="/bobo-logo.png" alt="BOBO Yazılım" className="h-20 w-auto object-contain" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Form Seçin</h1>
                <p className="text-gray-500">{location?.corporateName}</p>
                <p className="text-gray-500">{location?.name}</p>
              </div>

              <div className="space-y-3">
                {availableForms.filter(form => form.status === 1).map((form) => (
                  <button
                    key={form.id}
                    onClick={() => handleSelectForm(form)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${form.isPersonelLoginRequired
                      ? 'border-blue-300 bg-blue-50 hover:border-blue-400 hover:bg-blue-100'
                      : 'border-green-300 bg-green-50 hover:border-green-400 hover:bg-green-100'
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className={`font-semibold ${form.isPersonelLoginRequired ? 'text-blue-900' : 'text-green-900'}`}>
                          {form.title}
                        </h3>
                        <p className={`text-sm mt-1 ${form.isPersonelLoginRequired ? 'text-blue-700' : 'text-green-700'}`}>
                          {form.questions?.length || 0} soru
                        </p>
                        {form.isPersonelLoginRequired && (
                          <p className={`text-xs mt-2 px-2 py-1 inline-block rounded ${form.isPersonelLoginRequired ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-800'}`}>
                            Personel Giriş Gerekli
                          </p>
                        )}
                      </div>
                      <div className={`text-2xl ${form.isPersonelLoginRequired ? 'text-blue-300' : 'text-green-300'}`}>
                        →
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Form yükleniyor...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="inline-block bg-red-100 p-4 rounded-full mb-4">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Hata</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setTemplate(null);
                  loadFormData();
                }}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Tekrar Dene
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full mt-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
              >
                ← Geri Dön
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success State
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="inline-block bg-green-100 p-4 rounded-full mb-4">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Teşekkür Ederiz!</h2>
              <p className="text-gray-600 mb-2">Formunuz başarıyla gönderildi.</p>
              <p className="text-sm text-gray-500">Bu sayfayı kapatabilirsiniz.</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Form Display
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex flex-col">
      <div className="max-w-2xl mx-auto w-full">
        {/* Logged-in User Info */}
        {userData && selectedFormRequiresLogin && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{userData.name}</p>
                <p className="text-xs text-gray-600">{userData.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
            >
              Çıkış Yap
            </button>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${selectedFormRequiresLogin ? 'bg-blue-100' : 'bg-green-100'}`}>
              {selectedFormRequiresLogin
                ? <Users className="w-8 h-8 text-blue-600" />
                : <User className="w-8 h-8 text-green-600" />
              }
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-800">{template?.title}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Building2 className="w-4 h-4" />
                <span>{location?.corporateName}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <MapPin className="w-4 h-4" />
                <span>{location?.name}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
          {template?.description && (
            <p className="text-sm text-gray-600 mb-6 whitespace-pre-wrap">{template.description}</p>
          )}
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-indigo-600" />
            Sorular
          </h3>

          <div className="space-y-6">
            {template?.questions?.map((question, index) => (
              <div key={question.id} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                <div className="flex items-start gap-3 mb-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">
                      {question.questionText}
                      {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </p>
                  </div>
                </div>

                {/* Question Type: Open Text */}
                {question.questionType === 1 && (
                  <div className="ml-9">
                    <textarea
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      placeholder="Cevabınızı yazın..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    />
                  </div>
                )}

                {/* Question Type: Rating */}
                {question.questionType === 2 && (
                  <div className="ml-9 flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleAnswerChange(question.id, star)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star
                          size={36}
                          fill={star <= (answers[question.id] || 0) ? '#FCD34D' : 'none'}
                          stroke={star <= (answers[question.id] || 0) ? '#FCD34D' : '#D1D5DB'}
                          className="transition-colors"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* Question Type: Multiple Choice */}
                {question.questionType === 3 && (
                  <div className="ml-9 space-y-2">
                    {question.options?.map((option) => {
                      const optionId = typeof option === 'object' ? option.id : option;
                      const optionText = typeof option === 'object' ? option.optionText : option;
                      return (
                        <button
                          key={optionId}
                          onClick={() => handleAnswerChange(question.id, optionId)}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all ${answers[question.id] === optionId
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          {optionText}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Photo Upload */}
                {question.photoRequirement > 1 && (
                  <div className="ml-9 mt-3">
                    {!photos[question.id] ? (
                      <label className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${PHOTO_REQUIREMENTS[question.photoRequirement] === 'required'
                        ? 'border-red-300 hover:border-red-400 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400'
                        }`}>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handlePhotoUpload(question.id, e)}
                          className="hidden"
                        />
                        <Camera className={PHOTO_REQUIREMENTS[question.photoRequirement] === 'required' ? 'text-red-500' : 'text-gray-500'} size={20} />
                        <span className={PHOTO_REQUIREMENTS[question.photoRequirement] === 'required' ? 'text-red-600' : 'text-gray-600'}>
                          {PHOTO_REQUIREMENTS[question.photoRequirement] === 'required' ? 'Fotoğraf Ekle (Zorunlu)' : 'Fotoğraf Ekle (İsteğe Bağlı)'}
                        </span>
                      </label>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle className="text-green-600" size={20} />
                        <span className="text-green-700 flex-1">Fotoğraf yüklendi</span>
                        <button
                          onClick={() => removePhoto(question.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!isFormComplete() || submitting}
          className={`w-full py-4 rounded-xl font-semibold text-lg shadow-lg transition-all flex items-center justify-center gap-2 ${isFormComplete() && !submitting
            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-xl hover:scale-[1.02]'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          <Send size={20} />
          {submitting ? 'Gönderiliyor...' : 'Formu Gönder'}
        </button>

        {!isFormComplete() && (
          <p className="text-center text-sm text-gray-500 mt-3">
            * Tüm zorunlu alanları doldurun
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
};

// QR Scanner Component
const QRScanner = ({ onQRDetected, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stream = null;

    const initCamera = async () => {
      try {
        // Request camera permission and use back camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment', // Back camera
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            startScanning();
          };
        }
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          setError('Kamera izni reddedildi. Lütfen tarayıcı ayarlarınızdan izin verin.');
        } else if (err.name === 'NotFoundError') {
          setError('Kamera bulunamadı.');
        } else {
          setError('Kamera açılamadı. Lütfen tekrar deneyin.');
        }
      }
    };

    const startScanning = () => {
      const scanFrame = () => {
        if (!videoRef.current || !canvasRef.current || !scanning) return;

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        const video = videoRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        try {
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          // Simple QR code detection - look for the QR pattern
          // This is a simplified approach - check if we can decode
          const decoded = decodeQRFromCanvas(canvas);
          if (decoded) {
            setScanning(false);
            onQRDetected(decoded);
            return;
          }
        } catch (e) {
          // Continue scanning on error
        }

        if (scanning) {
          requestAnimationFrame(scanFrame);
        }
      };

      requestAnimationFrame(scanFrame);
    };

    const decodeQRFromCanvas = (canvas) => {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Use jsQR to detect QR code
      const code = jsQR(imageData.data, canvas.width, canvas.height);

      if (code) {
        let scannedData = code.data;

        // Extract GUID from URL format: http://temizlikgeribildirim.com/form/GUID
        if (scannedData.includes('/form/')) {
          scannedData = scannedData.split('/form/')[1];
        }

        return scannedData;
      }
      return null;
    };

    initCamera();

    return () => {
      setScanning(false);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [scanning, onQRDetected]);

  // Since simple canvas-based QR detection is limited, use input fallback
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">QR Kodu Okut</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <video
              ref={videoRef}
              className="w-full rounded-lg bg-black"
              style={{ aspectRatio: '4/3' }}
            />
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Home Page - Landing page when no QR code
const HomePage = () => {
  const navigate = useNavigate();
  const [showScanner, setShowScanner] = useState(false);

  const handleStartScan = () => {
    setShowScanner(true);
  };

  const handleQRDetected = (qrCode) => {
    navigate(`/form/${qrCode}`);
    setShowScanner(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <img src="/bobo-logo.png" alt="BOBO Yazılım" className="h-24 w-auto object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Temizlik Geri Bildirim Sistemi</h1>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 mb-6">
              <QrCode className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
              <p className="text-gray-700 font-medium">Form doldurmak için QR kodu okutun</p>
              <p className="text-sm text-gray-500 mt-2">Lütfen cihazınızın kamera izni hakkındaki soruya "İzin Ver" ile yanıt verin.</p>
            </div>

            <button
              onClick={handleStartScan}
              className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <Camera className="w-5 h-5" />
              QR Kodu Okut
            </button>
          </div>
        </div>
      </div>
      <Footer />

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner
          onQRDetected={handleQRDetected}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

// Main App with Router
const App = () => {
  return (
    <BrowserRouter basename="/">
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/form/:qrCode" element={<FormPage />} />
        <Route path="/" element={<Navigate to="/admin/login" replace />} />
        <Route path="/*" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;